import { ReactNode } from 'react';

import { K8sProvider } from '@cast/shared-types';
import {
  AKSCluster,
  Cluster,
  EKSCluster,
  GKECluster,
  KopsCluster,
} from '@cast/types';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type BaseProps = {
  noCluster?: ReactNode;
  fallback?: ReactNode;
  isNot?: boolean;
};

type ProviderToCluster<T extends `${K8sProvider}` | `${K8sProvider}`[]> =
  T extends `${K8sProvider.EKS}`
    ? EKSCluster
    : T extends `${K8sProvider.AKS}`
    ? AKSCluster
    : T extends `${K8sProvider.GKE}`
    ? GKECluster
    : T extends `${K8sProvider.KOPS}`
    ? KopsCluster
    : T extends Array<`${K8sProvider}`>
    ? Cluster
    : never;

type RootProps<T extends `${K8sProvider}` | Array<`${K8sProvider}`>> = {
  provider: T;
  children: ((cluster: ProviderToCluster<T>) => ReactNode) | ReactNode;
};

type Props<T extends `${K8sProvider}` | Array<`${K8sProvider}`>> = BaseProps &
  RootProps<T>;

type RenderContentProps = {
  provider: `${K8sProvider}`;
  cluster: Cluster;
  fallback: ReactNode;
  children: ReactNode | ((cluster: Cluster) => ReactNode);
  isNot?: boolean;
};

const renderContent = ({
  provider,
  cluster,
  fallback,
  children,
  isNot = false,
}: RenderContentProps) => {
  if (
    isNot
      ? provider !== cluster.providerType
      : provider === cluster.providerType
  ) {
    if (typeof children === 'function') {
      return <>{children(cluster)}</>;
    }
    return <>{children}</>;
  }
  return <>{fallback}</>;
};

export const WhenProvider = <
  T extends `${K8sProvider}` | Array<`${K8sProvider}`>
>({
  provider,
  children,
  noCluster = null,
  fallback = null,
  isNot = false,
}: Props<T>) => {
  const { cluster } = useCluster();
  if (!cluster) {
    return <>{noCluster || fallback}</>;
  }

  if (Array.isArray(provider)) {
    const clusterProvider = cluster.providerType;
    if (
      isNot
        ? !provider.includes(clusterProvider)
        : provider.includes(clusterProvider)
    ) {
      if (typeof children === 'function') {
        return <>{children(cluster as never)}</>;
      }
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  return renderContent({
    provider,
    cluster,
    fallback,
    children: children as any,
    isNot,
  });
};
